import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { CheckCircle2, AlertTriangle, Clock, XCircle, Crown, CreditCard, ArrowRight, MessageCircle } from "lucide-react";

const API = `${window.location.origin}/api`;

function formatPreco(v) {
  if (v == null) return "Sob consulta";
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

const STATUS_META = {
  authorized: { label: "Assinatura ativa", cor: "emerald", Icon: CheckCircle2 },
  active: { label: "Assinatura ativa", cor: "emerald", Icon: CheckCircle2 },
  trial: { label: "Período de teste", cor: "amber", Icon: Clock },
  pending: { label: "Aguardando pagamento", cor: "blue", Icon: AlertTriangle },
  cancelled: { label: "Assinatura cancelada", cor: "red", Icon: XCircle },
  paused: { label: "Assinatura pausada", cor: "gray", Icon: AlertTriangle },
  super: { label: "Acesso ilimitado (Super Admin)", cor: "purple", Icon: Crown },
};

export default function MinhaAssinaturaPage() {
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [pagamentos, setPagamentos] = useState([]);
  const [loadingPagamentos, setLoadingPagamentos] = useState(false);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/planos/meu`);
      setDados(data);
    } catch (e) {
      toast.error("Erro ao carregar dados da assinatura");
    } finally {
      setLoading(false);
    }
    // Carrega histórico de cobranças em paralelo (não bloqueia UI principal)
    setLoadingPagamentos(true);
    try {
      const { data } = await axios.get(`${API}/planos/pagamentos`);
      setPagamentos(data.pagamentos || []);
    } catch {
      setPagamentos([]);
    } finally {
      setLoadingPagamentos(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  if (loading || !dados) {
    return <div className="text-center py-12 text-gray-500" data-testid="assinatura-loading">Carregando assinatura…</div>;
  }

  const plano = dados.plano || {};
  const trial = dados.trial;
  const subStatus = dados.subscription_status;
  const isSuper = !!dados.super_admin;
  const jaAssinado = subStatus === "authorized" || subStatus === "active";
  const emTrial = !!trial?.ativo;
  const estado = isSuper ? "super" : (jaAssinado ? "authorized" : emTrial ? "trial" : (subStatus || "trial"));
  const meta = STATUS_META[estado] || STATUS_META.trial;
  const CorIcon = meta.Icon;

  const corBadge = {
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-300",
    amber: "bg-amber-100 text-amber-800 border-amber-300",
    blue: "bg-blue-100 text-blue-800 border-blue-300",
    red: "bg-red-100 text-red-800 border-red-300",
    gray: "bg-gray-100 text-gray-700 border-gray-300",
    purple: "bg-purple-100 text-purple-800 border-purple-300",
  }[meta.cor];

  const irParaCheckout = async () => {
    if (!plano.id || plano.id === "corporativo" || plano.id === "start") {
      navigate("/planos");
      return;
    }
    setCheckoutLoading(true);
    try {
      const { data } = await axios.post(`${API}/planos/checkout`, { plano: plano.id });
      if (data.status === "checkout_criado" && data.init_point) {
        window.location.href = data.init_point;
        return;
      }
      if (data.status === "contato_comercial" && data.whatsapp) {
        window.open(data.whatsapp, "_blank");
        return;
      }
      toast.info(data.message || "Não foi possível iniciar o checkout. Vá em Planos para escolher.");
      navigate("/planos");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao iniciar pagamento");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const cancelar = async () => {
    setCancelando(true);
    try {
      const { data } = await axios.put(`${API}/planos/cancelar`);
      toast.success(data.message || "Assinatura cancelada.");
      setCancelOpen(false);
      await carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao cancelar assinatura");
    } finally {
      setCancelando(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="minha-assinatura-page">
      <div>
        <h1 className="text-3xl font-extrabold flex items-center gap-2" style={{ fontFamily: "Work Sans" }}>
          <CreditCard className="h-7 w-7 text-blue-700" /> Minha assinatura
        </h1>
        <p className="text-sm text-gray-500 mt-1">Gerencie seu plano, pagamento e status de cobrança</p>
      </div>

      {/* Card principal */}
      <Card className="border-2 border-blue-100">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center">
                <Crown className="h-6 w-6" />
              </div>
              <div>
                <p className="text-xs uppercase font-bold tracking-wider text-gray-500">Plano vigente</p>
                <CardTitle className="text-2xl">{plano.nome || "—"}</CardTitle>
              </div>
            </div>
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-bold ${corBadge}`} data-testid="assinatura-status-badge">
              <CorIcon className="h-4 w-4" /> {meta.label}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-slate-50 border rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase font-bold">Mensalidade</p>
              <p className="text-xl font-extrabold mt-1">{formatPreco(plano.preco)}</p>
            </div>
            <div className="bg-slate-50 border rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase font-bold">Carros / Rotas</p>
              <p className="text-xl font-extrabold mt-1">
                {dados.uso?.carros_atuais ?? 0}
                <span className="text-sm text-gray-500 font-medium">
                  {" "}/ {plano.max_carros == null ? "∞" : plano.max_carros}
                </span>
              </p>
            </div>
            <div className="bg-slate-50 border rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase font-bold">
                {emTrial ? "Termina em" : jaAssinado ? "Renovação" : "Status"}
              </p>
              <p className="text-xl font-extrabold mt-1">
                {emTrial
                  ? new Date(trial.termina_em).toLocaleDateString("pt-BR")
                  : jaAssinado
                  ? "Automática"
                  : "—"}
              </p>
              {emTrial && (
                <p className="text-xs text-amber-700 mt-0.5">
                  Faltam {trial.dias_restantes} dia(s)
                </p>
              )}
            </div>
          </div>

          {/* Painel de ação */}
          {isSuper && (
            <div className="rounded-xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-fuchsia-50 p-4">
              <p className="font-bold text-purple-900 flex items-center gap-2">
                <Crown className="h-5 w-5" /> Acesso ilimitado
              </p>
              <p className="text-sm text-purple-800 mt-1">
                Como Super Admin, você tem acesso completo a todos os recursos sem cobrança. Assinaturas se aplicam apenas aos assinantes do SaaS.
              </p>
            </div>
          )}

          {!isSuper && emTrial && (
            <div className="rounded-xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-4">
              <p className="font-bold text-amber-900 flex items-center gap-2">
                <Clock className="h-5 w-5" /> Você está no período de teste
              </p>
              <p className="text-sm text-amber-800 mt-1">
                Continue aproveitando o plano <strong>{plano.nome}</strong> sem interrupção — assine agora e o débito só começa após o teste.
              </p>
              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <Button
                  onClick={irParaCheckout}
                  disabled={checkoutLoading}
                  className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 gap-2"
                  data-testid="btn-assinar-agora"
                >
                  {checkoutLoading ? "Abrindo checkout…" : <>Assinar agora no Mercado Pago <ArrowRight className="h-4 w-4" /></>}
                </Button>
                <Button variant="outline" onClick={() => navigate("/planos")} data-testid="btn-mudar-plano">
                  Mudar de plano
                </Button>
              </div>
            </div>
          )}

          {!isSuper && jaAssinado && (
            <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-4">
              <p className="font-bold text-emerald-900 flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5" /> Assinatura ativa
              </p>
              <p className="text-sm text-emerald-800 mt-1">
                Obrigado por confiar no Compromisso Digital! Sua cobrança é automática pelo Mercado Pago.
              </p>
              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => navigate("/planos")} data-testid="btn-alterar-plano">
                  Alterar plano
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setCancelOpen(true)}
                  className="gap-2"
                  data-testid="btn-cancelar"
                >
                  <XCircle className="h-4 w-4" /> Cancelar assinatura
                </Button>
              </div>
            </div>
          )}

          {!isSuper && !emTrial && !jaAssinado && (
            <div className="rounded-xl border-2 border-red-200 bg-red-50 p-4">
              <p className="font-bold text-red-900 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" /> {estado === "cancelled" ? "Sua assinatura está cancelada" : "Você não tem assinatura ativa"}
              </p>
              <p className="text-sm text-red-800 mt-1">
                Assine agora para liberar todos os recursos do plano.
              </p>
              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <Button
                  onClick={irParaCheckout}
                  disabled={checkoutLoading}
                  className="bg-blue-600 hover:bg-blue-700 gap-2"
                  data-testid="btn-reativar"
                >
                  {checkoutLoading ? "Abrindo checkout…" : <>Assinar agora <ArrowRight className="h-4 w-4" /></>}
                </Button>
                <Button variant="outline" onClick={() => navigate("/planos")} data-testid="btn-ver-planos">
                  Ver planos
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Histórico de cobranças MP (só se não for super admin) */}
      {!isSuper && (
        <Card className="border-blue-100" data-testid="historico-cobrancas-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-blue-700" /> Histórico de cobranças
            </CardTitle>
            <p className="text-xs text-gray-500">Últimas 20 cobranças processadas pelo Mercado Pago</p>
          </CardHeader>
          <CardContent>
            {loadingPagamentos ? (
              <p className="text-sm text-gray-500 py-4 text-center">Carregando…</p>
            ) : pagamentos.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center" data-testid="historico-vazio">
                Nenhuma cobrança processada ainda. Após a primeira renovação automática, o histórico aparecerá aqui.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase text-gray-500">
                      <th className="py-2">Data</th>
                      <th className="py-2 text-right">Valor</th>
                      <th className="py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagamentos.map((p) => {
                      const st = (p.status || "").toLowerCase();
                      const stCor = {
                        approved: "bg-emerald-100 text-emerald-800",
                        authorized: "bg-emerald-100 text-emerald-800",
                        processed: "bg-emerald-100 text-emerald-800",
                        pending: "bg-amber-100 text-amber-800",
                        in_process: "bg-amber-100 text-amber-800",
                        recycling: "bg-blue-100 text-blue-800",
                        rejected: "bg-red-100 text-red-800",
                        cancelled: "bg-gray-100 text-gray-700",
                        refunded: "bg-purple-100 text-purple-800",
                      }[st] || "bg-gray-100 text-gray-700";
                      const stLabel = {
                        approved: "Pago", authorized: "Autorizado", processed: "Processado",
                        pending: "Pendente", in_process: "Em processamento", recycling: "Reprocessando",
                        rejected: "Recusado", cancelled: "Cancelado", refunded: "Reembolsado",
                      }[st] || st || "—";
                      const data = p.data || p.data_agendada;
                      const dataFmt = data ? new Date(data).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
                      return (
                        <tr key={p.id} className="border-b last:border-0" data-testid={`cobranca-row-${p.id}`}>
                          <td className="py-2.5 text-gray-800">{dataFmt}</td>
                          <td className="py-2.5 text-right font-semibold text-gray-900">{formatPreco(p.valor)}</td>
                          <td className="py-2.5">
                            <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-bold ${stCor}`}>{stLabel}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Suporte */}
      <div className="text-center text-xs text-gray-500">
        Precisa de ajuda?{" "}
        <a
          href="https://wa.me/5567991992593?text=Precisando+de+ajuda+com+minha+assinatura"
          target="_blank"
          rel="noreferrer"
          className="text-blue-700 font-medium hover:underline inline-flex items-center gap-1"
        >
          <MessageCircle className="h-3 w-3" /> Falar no WhatsApp
        </a>
      </div>

      {/* Dialog cancelar */}
      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent data-testid="dialog-cancelar-assinatura">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700 flex items-center gap-2">
              <XCircle className="h-5 w-5" /> Cancelar assinatura
            </AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que quer cancelar? Você mantém acesso até o fim do ciclo pago. Depois disso, os recursos premium são desativados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelando}>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={cancelar}
              disabled={cancelando}
              className="bg-red-600 hover:bg-red-700"
              data-testid="btn-confirmar-cancelar"
            >
              {cancelando ? "Cancelando…" : "Sim, cancelar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
